/**
 * Health check utility for server connectivity
 */

import { getGraphQLEndpoint } from './graphqlConfig'

export interface HealthCheckResult {
  healthy: boolean;
  status?: string;
  timestamp?: string;
  uptime?: number;
  database?: boolean;
  error?: string;
}

/**
 * Check server health via GraphQL
 */
export async function checkServerHealth(): Promise<HealthCheckResult> {
  try {
    // Use environment-aware GraphQL endpoint configuration
    const finalUrl = getGraphQLEndpoint()
    
    const response = await fetch(finalUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          query {
            health {
              status
              timestamp
              uptime
              environment
              checks {
                database
              }
            }
          }
        `,
      }),
    });

    if (!response.ok) {
      return {
        healthy: false,
        error: `Server returned ${response.status}: ${response.statusText}`,
      };
    }

    const result = await response.json();
    
    if (result.errors) {
      return {
        healthy: false,
        error: result.errors[0]?.message || 'GraphQL error',
      };
    }

    const data = result.data?.health;
    if (!data) {
      return {
        healthy: false,
        error: 'No health data returned',
      };
    }

    return {
      healthy: data.status === 'healthy',
      status: data.status,
      timestamp: data.timestamp,
      uptime: data.uptime,
      database: data.checks?.database || false,
    };
  } catch (error) {
    return {
      healthy: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Check GraphQL endpoint availability
 */
export async function checkGraphQLEndpoint(): Promise<boolean> {
  try {
    // Use environment-aware GraphQL endpoint configuration
    const finalUrl = getGraphQLEndpoint()
    
    const response = await fetch(finalUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: 'query { __typename }',
      }),
    });

    return response.ok;
  } catch (error) {
    return false;
  }
}

/**
 * Simple server availability check - just checks if server responds to HTTP requests
 * This is faster than a full GraphQL health check and works even if GraphQL isn't ready
 */
export async function checkServerAvailable(): Promise<boolean> {
  try {
    // Try to fetch the root or a simple endpoint
    // Use the same origin as the GraphQL endpoint
    const graphqlUrl = getGraphQLEndpoint();
    const baseUrl = graphqlUrl.startsWith('/') 
      ? window.location.origin 
      : new URL(graphqlUrl).origin;
    
    // Try fetching the root or API endpoint
    const response = await fetch(`${baseUrl}/api/v1`, {
      method: 'GET',
      credentials: 'include',
    });

    // Any response (even 404) means the server is up
    return response.status < 500;
  } catch (error) {
    // Network errors mean server is not available
    return false;
  }
}

